/** Clasificación de estatus comprador/usuario (espejo de backend/account_status.py). */

export const ACCOUNT_SECTIONS = {
  PLATFORM: "platform",
  PENDING: "pending",
  BUYER_ACCOUNT: "buyer_account",
  OPERATIONAL: "operational",
};

export const ACCOUNT_STATUS = {
  PLATFORM_DESIGNER: "platform_designer",
  PENDING_REGISTRATION: "pending_registration",
  INVITED_PENDING: "invited_pending",
  BUYER_TRIAL: "en_trial",
  BUYER_TRIAL_EXPIRED: "trial_vencido",
  BUYER_LICENSED: "licencia_activa",
  BUYER_LICENSE_REQUESTED: "licencia_solicitada",
  BUYER_OWNER: "comprador_titular",
  OPERATIONAL: "usuario_activo",
  BUYER_ADMIN: "buyer_admin_activo",
};

function hasLoginCredentials(user = {}) {
  return Boolean(user.has_password || user.has_pin);
}

/** Titular comprador SaaS — espejo backend saas_buyer_identity (sin I/O). */
export function isSaasCommercialBuyer(user = {}) {
  if (!user || user.is_designer_backdoor || user.is_buyer_tenant) return false;
  if (user.trial_bootstrap && !user.trial_onboarding_completed_at) return false;
  return Boolean(user.trial_onboarding_completed_at);
}

function isPlatformUser(user = {}) {
  if (user.role === "platform_designer" && isSaasCommercialBuyer(user)) {
    return false;
  }
  return user.role === "platform_designer" || Boolean(user.is_designer_backdoor);
}

function refineBuyerOwnerStatus(base, subscription) {
  if (!subscription) return base;
  const { mode, reason, ok } = subscription;
  if (ok && mode === "licensed") {
    return {
      code: ACCOUNT_STATUS.BUYER_LICENSED,
      label: "Licencia activa",
      section: ACCOUNT_SECTIONS.BUYER_ACCOUNT,
      is_operational: true,
      suggested_action: "Operar según módulos contratados",
    };
  }
  if (reason === "trial_expired" || (mode === "trial" && ok === false)) {
    return {
      code: ACCOUNT_STATUS.BUYER_TRIAL_EXPIRED,
      label: "Periodo de prueba vencido",
      section: ACCOUNT_SECTIONS.BUYER_ACCOUNT,
      is_operational: true,
      suggested_action: "Solicitar o activar licencia",
    };
  }
  if (mode === "trial" && ok === true) {
    return {
      code: ACCOUNT_STATUS.BUYER_TRIAL,
      label: "En periodo de prueba",
      section: ACCOUNT_SECTIONS.BUYER_ACCOUNT,
      is_operational: true,
      suggested_action: "Mostrar días restantes",
    };
  }
  if (!ok && ["expired", "invalid_hardware", "invalid_token"].includes(reason)) {
    return {
      code: ACCOUNT_STATUS.BUYER_LICENSE_REQUESTED,
      label: "Licencia inactiva o pendiente",
      section: ACCOUNT_SECTIONS.BUYER_ACCOUNT,
      is_operational: true,
      suggested_action: "Revisar solicitud / activar licencia",
    };
  }
  return base;
}

/** @param {object} user @param {object} [subscription] */
export function classifyAccountStatus(user = {}, subscription = null) {
  if (isPlatformUser(user)) {
    return {
      code: ACCOUNT_STATUS.PLATFORM_DESIGNER,
      label: "Diseñador / plataforma",
      section: ACCOUNT_SECTIONS.PLATFORM,
      is_operational: true,
      suggested_action: "Herramientas administrativas",
    };
  }
  if (
    user.needs_trial_registration
    || (user.trial_bootstrap && !user.trial_onboarding_completed_at)
  ) {
    return {
      code: ACCOUNT_STATUS.PENDING_REGISTRATION,
      label: "Solicitud / registro pendiente",
      section: ACCOUNT_SECTIONS.PENDING,
      is_operational: false,
      suggested_action: "Completar registro o revisar solicitud",
    };
  }
  if (!hasLoginCredentials(user)) {
    return {
      code: ACCOUNT_STATUS.INVITED_PENDING,
      label: "Invitado sin acceso",
      section: ACCOUNT_SECTIONS.PENDING,
      is_operational: false,
      suggested_action: "Definir contraseña o invitación",
    };
  }
  if (user.is_buyer_tenant && user.role === "buyer_admin") {
    return {
      code: ACCOUNT_STATUS.BUYER_ADMIN,
      label: "Administrador comprador (portal)",
      section: ACCOUNT_SECTIONS.OPERATIONAL,
      is_operational: true,
      suggested_action: "Gestionar auxiliares",
    };
  }
  if (user.is_admin_owner) {
    const base = {
      code: ACCOUNT_STATUS.BUYER_OWNER,
      label: "Titular / comprador",
      section: ACCOUNT_SECTIONS.BUYER_ACCOUNT,
      is_operational: true,
      suggested_action: "Revisar trial o licencia",
    };
    return refineBuyerOwnerStatus(base, subscription);
  }
  return {
    code: ACCOUNT_STATUS.OPERATIONAL,
    label: "Usuario activo",
    section: ACCOUNT_SECTIONS.OPERATIONAL,
    is_operational: true,
    suggested_action: "Operación normal",
  };
}

/** Usa account_status del API si viene; si no, clasifica en cliente. */
export function resolveAccountStatus(user = {}) {
  if (user.account_status && user.account_status.code) {
    return user.account_status;
  }
  return classifyAccountStatus(user, user.subscription || null);
}

export function groupUsersBySection(users = []) {
  const groups = {
    [ACCOUNT_SECTIONS.OPERATIONAL]: [],
    [ACCOUNT_SECTIONS.BUYER_ACCOUNT]: [],
    [ACCOUNT_SECTIONS.PENDING]: [],
    [ACCOUNT_SECTIONS.PLATFORM]: [],
  };
  users.forEach((u) => {
    const section = resolveAccountStatus(u).section || ACCOUNT_SECTIONS.OPERATIONAL;
    if (!groups[section]) groups[section] = [];
    groups[section].push(u);
  });
  return groups;
}

/**
 * Titular/comprador que puede instalar licencia o enviar solicitud comercial (espejo backend).
 * Incluye `role=admin` sin `is_admin_owner` (cuentas SaaS legacy o migración incompleta).
 */
export function canInstallLicenseForWorkspace(user = {}) {
  if (!user) return false;
  return Boolean(
    user.is_admin_owner
    || user.is_buyer_tenant
    || (user.role === "admin" && !user.is_buyer_tenant),
  );
}

/**
 * Guard duro de contenedores UI (Panel, Configuración): ocultar CTAs comprador cuando
 * opera como diseñador BNT. Comprador SaaS titular no se suprime aunque traiga
 * is_platform_designer residual; con buyerPreviewUx el diseñador ve CTAs de prueba.
 * @param {object} user
 * @param {{ buyerPreviewUx?: boolean }} [opts]
 */
export function shouldSuppressLicenseCtasForPlatformDesigner(user = {}, opts = {}) {
  if (!user) return true;
  if (opts.buyerPreviewUx) return false;
  const designerRole =
    user.is_platform_designer || user.role === "platform_designer";
  if (!designerRole) return false;
  if (isSaasCommercialBuyer(user)) return false;
  return true;
}

export function isBuyerLicenseCtaAudience(user = {}, systemInfo = null, opts = {}) {
  if (!user) return false;
  const buyerPreviewUx = Boolean(opts.buyerPreviewUx);
  if (!buyerPreviewUx && isPlatformUser(user)) return false;
  const sub = systemInfo?.subscription || {};
  if (
    !buyerPreviewUx
    && sub.mode === "platform_designer"
    && !isSaasCommercialBuyer(user)
  ) {
    return false;
  }
  return canInstallLicenseForWorkspace(user);
}
